/**
 * Portal main chart (monthly points) and amortization table (yearly rows).
 * Extracted from PortalPage for maintainability; behavior must match previous inline useMemos.
 */
import { getAnnualRateForPlan } from "./loanConstants";
import { INVESTMENT_RATE_MONTHLY } from "./loanConstants";
import { sampleAtMonth } from "./usePortalCalculations";

export function buildPortalYearlyChartData({
  schedule,
  payoffMonths,
  effectivePlanYears,
  totalTermYears,
  totalTermMonths,
  enableDelay,
  delayYears,
  delayMonths,
  enableGrowth,
  gr,
  igr,
  enableInvestment,
  monthlyInvestment,
  startingInvestmentCapital,
  interestAtEnd,
  payoffByInvestment,
  constrainedMonthlyPayment,
}) {
  const data = [];
  const endYear = payoffMonths / 12;
  const delayMonthsTotal = enableDelay ? delayYears * 12 + delayMonths : 0;

  const calculateInvestmentValueAtMonth = (targetMonth) => {
    if (!enableInvestment) return 0;

    const startingCapital = Number(startingInvestmentCapital) || 0;
    let investmentValue = startingCapital;

    for (let month = 0; month < targetMonth; month++) {
      const isInDelayPeriod = month < delayMonthsTotal;

      if (isInDelayPeriod) {
        if (investmentValue > 0) {
          investmentValue = investmentValue * (1 + INVESTMENT_RATE_MONTHLY);
        }
      } else {
        let investmentContribution = monthlyInvestment;
        if (enableGrowth) {
          const yearsElapsed = Math.floor((month - delayMonthsTotal) / 12);
          investmentContribution = monthlyInvestment * Math.pow(1 + igr, Math.max(0, yearsElapsed));
        }
        const invest_contrib_t = enableInvestment ? investmentContribution : 0;

        investmentValue = investmentValue * (1 + INVESTMENT_RATE_MONTHLY) + invest_contrib_t;
      }
    }

    return investmentValue;
  };

  const sampleChartAtMonth = (mi) => {
    let before = null;
    let after = null;
    let beforeIdx = -1;

    for (let i = schedule.length - 1; i >= 0; i--) {
      if (schedule[i].m <= mi) {
        before = schedule[i];
        beforeIdx = i;
        break;
      }
    }

    if (beforeIdx >= 0 && beforeIdx < schedule.length - 1) {
      after = schedule[beforeIdx + 1];
    }

    if (!before && !after) return { balance: 0, interestCumulative: 0, investmentValue: 0 };

    let investmentValue = 0;
    if (enableInvestment) {
      investmentValue = calculateInvestmentValueAtMonth(mi);
    } else {
      if (before && after) {
        const t = (mi - before.m) / Math.max(1, after.m - before.m);
        investmentValue =
          (before.investmentValue || 0) +
          ((after.investmentValue || 0) - (before.investmentValue || 0)) * t;
      } else if (before) {
        investmentValue = before.investmentValue || 0;
      } else if (after) {
        investmentValue = after.investmentValue || 0;
      }
    }

    if (!before) {
      return {
        balance: after.balance,
        interestCumulative: after.interestCumulative,
        investmentValue,
      };
    }
    if (!after) {
      return {
        balance: before.balance,
        interestCumulative: before.interestCumulative,
        investmentValue,
      };
    }
    const t = (mi - before.m) / Math.max(1, after.m - before.m);
    return {
      balance: before.balance + (after.balance - before.balance) * t,
      interestCumulative: before.interestCumulative + (after.interestCumulative - before.interestCumulative) * t,
      investmentValue,
    };
  };

  const maxTermMonths =
    enableDelay && (delayYears > 0 || delayMonths > 0)
      ? totalTermYears * 12 + totalTermMonths
      : effectivePlanYears * 12;
  const maxMonths = maxTermMonths;

  for (let month = 0; month <= maxMonths; month++) {
    if (month > payoffMonths && !(enableInvestment && payoffByInvestment)) break;
    const s = sampleChartAtMonth(month);

    let principalValue = s.balance;
    if (enableInvestment && payoffByInvestment) {
      if (month >= payoffMonths && s.balance === 0 && s.investmentValue > 0) {
        principalValue = s.investmentValue;
      } else if (
        s.investmentValue >= s.balance &&
        s.balance > 0 &&
        month >= payoffMonths - 1
      ) {
        principalValue = s.balance;
      }
    }

    let payment = 0;
    if (month >= delayMonthsTotal) {
      payment = constrainedMonthlyPayment;
      if (enableGrowth) {
        const yearsElapsed = Math.floor((month - delayMonthsTotal) / 12);
        payment = constrainedMonthlyPayment * Math.pow(1 + gr, yearsElapsed);
      }
    }

    let finalPrincipal = Math.max(0, principalValue);
    if (enableInvestment && payoffByInvestment && month >= payoffMonths && s.balance === 0) {
      finalPrincipal = s.investmentValue || 0;
    }

    data.push({
      year: month / 12,
      principal: finalPrincipal,
      interest: s.interestCumulative,
      investment: s.investmentValue,
      payment,
    });

    if (enableInvestment && payoffByInvestment && month >= payoffMonths) {
      break;
    }
    if (enableInvestment && !payoffByInvestment && s.investmentValue >= s.balance && s.balance > 0) {
      break;
    }
  }

  const payoffMonthsIsInteger = Math.abs(payoffMonths - Math.round(payoffMonths)) < 1e-9;
  const lastMonthInData = data.length > 0 ? data[data.length - 1].year * 12 : -1;
  const finalMonthAlreadyIncluded = Math.abs(lastMonthInData - payoffMonths) < 1e-9;

  if (!payoffMonthsIsInteger || !finalMonthAlreadyIncluded) {
    const sEnd = sampleChartAtMonth(payoffMonths);

    let finalPrincipal;
    let finalInvestment;
    if (enableInvestment && payoffByInvestment) {
      finalPrincipal = sEnd.investmentValue || 0;
      finalInvestment = sEnd.investmentValue || 0;
    } else {
      finalPrincipal = 0;
      finalInvestment = sEnd.investmentValue || 0;
    }

    let finalPayment = 0;
    if (payoffMonths >= delayMonthsTotal) {
      finalPayment = constrainedMonthlyPayment;
      if (enableGrowth) {
        const yearsElapsed = Math.floor((payoffMonths - delayMonthsTotal) / 12);
        finalPayment = constrainedMonthlyPayment * Math.pow(1 + gr, yearsElapsed);
      }
    }

    const lastRowPrincipal = data.length > 0 ? data[data.length - 1].principal || 0 : 0;
    const loanAlreadyPaidOff = lastRowPrincipal <= 0.01 && !(enableInvestment && payoffByInvestment);

    if (enableInvestment && payoffByInvestment) {
      data.push({
        year: endYear,
        principal: finalPrincipal,
        interest: interestAtEnd || sEnd.interestCumulative,
        investment: finalInvestment,
        payment: finalPayment,
      });
    } else if (!loanAlreadyPaidOff) {
      data.push({
        year: endYear,
        principal: finalPrincipal,
        interest: interestAtEnd || sEnd.interestCumulative,
        investment: finalInvestment,
        payment: finalPayment,
      });
    }
  }

  return data;
}

export function buildPortalYearlyTableData({
  schedule,
  payoffMonths,
  effectivePlanYears,
  totalTermYears,
  totalDebt,
  planYears,
  enableDelay,
  delayYears,
  delayMonths,
  enableGrowth,
  gr,
  igr,
  enableInvestment,
  monthlyInvestment,
  startingInvestmentCapital,
  interestAtEnd,
  finalInvestmentValue,
  payoffByInvestment,
  constrainedMonthlyPayment,
}) {
  const data = [];
  const endYear = payoffMonths / 12;
  const loanMonthlyRate = getAnnualRateForPlan(planYears) / 12;

  const maxYearsForData =
    enableDelay && (delayYears > 0 || delayMonths > 0) ? totalTermYears : effectivePlanYears;
  const delayMonthsTotal = enableDelay ? delayYears * 12 + delayMonths : 0;
  const maxMonthsForData = maxYearsForData * 12 + (enableDelay ? delayMonths : 0);

  let debtAtBeginningOfYear = Math.max(0, Number(totalDebt) || 0);
  const payoffYearCap = Math.max(0, Math.floor((payoffMonths - 1) / 12));

  for (let yr = 0; yr <= maxYearsForData; yr++) {
    if (yr > payoffYearCap) break;
    const yearStartMonth = yr * 12;
    const yearEndMonthIndex = Math.min((yr + 1) * 12, payoffMonths);

    if (yearStartMonth > maxMonthsForData && yearStartMonth > payoffMonths) break;

    const isDelayYear = enableDelay && yearStartMonth < delayMonthsTotal;

    let payment = 0;
    if (!isDelayYear) {
      payment = constrainedMonthlyPayment;
      if (enableGrowth) {
        const yearsElapsed = Math.max(0, yr - Math.floor(delayMonthsTotal / 12));
        payment = constrainedMonthlyPayment * Math.pow(1 + gr, yearsElapsed);
      }
    }

    const firstPaymentMonth = delayMonthsTotal;
    const yearStartForPayments = Math.max(yearStartMonth, firstPaymentMonth);
    const isPayoffYear = yr === payoffYearCap;
    const actualEndMonthForPayments = isPayoffYear ? payoffMonths : yearEndMonthIndex;
    const monthsPaidThisYear = isDelayYear ? 0 : Math.max(0, actualEndMonthForPayments - yearStartForPayments);

    const monthlyPaymentForYear = payment;
    let investmentContribution = monthlyInvestment;
    if (enableGrowth) {
      const yearsElapsed = Math.max(0, yr - Math.floor(delayMonthsTotal / 12));
      investmentContribution = monthlyInvestment * Math.pow(1 + igr, yearsElapsed);
    }
    const monthlyInvestmentContribution = enableInvestment ? investmentContribution : 0;
    const monthlyDuoPayment = monthlyPaymentForYear;

    const totalDuoPaymentsThisYear = monthlyDuoPayment * monthsPaidThisYear;
    const totalPaidThisYear = monthlyPaymentForYear * monthsPaidThisYear;

    let endRow = null;
    for (let i = 0; i < schedule.length; i++) {
      if (schedule[i].m === yearEndMonthIndex) {
        endRow = schedule[i];
        break;
      }
    }
    if (!endRow && schedule.length > 0) {
      for (let i = schedule.length - 1; i >= 0; i--) {
        if (schedule[i].m <= yearEndMonthIndex) {
          endRow = schedule[i];
          break;
        }
      }
    }

    let startRow = null;
    for (let i = 0; i < schedule.length; i++) {
      if (schedule[i].m === yearStartMonth) {
        startRow = schedule[i];
        break;
      }
    }
    if (!startRow && schedule.length > 0) {
      for (let i = schedule.length - 1; i >= 0; i--) {
        if (schedule[i].m <= yearStartMonth) {
          startRow = schedule[i];
          break;
        }
      }
    }

    const cumulativeInterestAtYearEnd = endRow ? endRow.interestCumulative : 0;
    const cumulativeInterestAtYearStart = startRow ? startRow.interestCumulative : 0;
    const totalInterestThisYear = cumulativeInterestAtYearEnd - cumulativeInterestAtYearStart;

    let debtAtEndOfYear;
    if (endRow) {
      debtAtEndOfYear = Math.max(0, endRow.balance);
    } else if (startRow) {
      debtAtEndOfYear = Math.max(0, startRow.balance);
    } else {
      const monthsInYear = monthsPaidThisYear;
      let runningBalance = debtAtBeginningOfYear;
      for (let month = 0; month < monthsInYear; month++) {
        const monthlyInterest = runningBalance * loanMonthlyRate;
        if (monthlyDuoPayment >= monthlyInterest) {
          runningBalance = runningBalance - (monthlyDuoPayment - monthlyInterest);
        } else {
          runningBalance = runningBalance + (monthlyInterest - monthlyDuoPayment);
        }
      }
      debtAtEndOfYear = Math.max(0, runningBalance);
    }

    const hasStartKapitaal = startingInvestmentCapital && Number(startingInvestmentCapital) > 0;

    let investmentValueAtYearEnd = 0;

    if (yearEndMonthIndex < firstPaymentMonth) {
      if (hasStartKapitaal) {
        let E = Number(startingInvestmentCapital);
        for (let month = 0; month < yearEndMonthIndex; month++) {
          E = E * (1 + INVESTMENT_RATE_MONTHLY);
        }
        investmentValueAtYearEnd = E;
      } else {
        investmentValueAtYearEnd = 0;
      }
    } else {
      const actualEndMonth = isPayoffYear ? payoffMonths : yearEndMonthIndex;
      const monthsInvested = Math.max(0, actualEndMonth - firstPaymentMonth);

      if (monthsInvested > 0) {
        let E = 0;

        if (yr === 0) {
          E = hasStartKapitaal ? Number(startingInvestmentCapital) : 0;

          if (hasStartKapitaal && delayMonthsTotal > 0) {
            for (let month = 0; month < delayMonthsTotal; month++) {
              E = E * (1 + INVESTMENT_RATE_MONTHLY);
            }
          }
        } else {
          if (data.length > 0) {
            const prevYearData = data[data.length - 1];
            E = prevYearData.investment || 0;
          } else {
            const startSample = sampleAtMonth(schedule, yearStartMonth);
            E = startSample.investmentValue || 0;
          }
        }

        const monthsToIterate = isPayoffYear
          ? Math.max(0, payoffMonths - yearStartForPayments)
          : Math.max(0, actualEndMonth - yearStartForPayments);

        for (let month = 0; month < monthsToIterate; month++) {
          E = E * (1 + INVESTMENT_RATE_MONTHLY) + monthlyInvestmentContribution;

          const currentMonthInYear = yearStartForPayments + month;
          const currentSample = sampleAtMonth(schedule, currentMonthInYear);
          const currentBalance = currentSample.balance || 0;

          if (enableInvestment && E > currentBalance && currentBalance > 0) {
            E = currentBalance;
          }
        }

        investmentValueAtYearEnd = E;

        if (enableInvestment && investmentValueAtYearEnd > debtAtEndOfYear && debtAtEndOfYear > 0.01) {
          debtAtEndOfYear = 0;
        }
        if (enableInvestment && debtAtEndOfYear > 0.01 && investmentValueAtYearEnd > debtAtEndOfYear) {
          investmentValueAtYearEnd = debtAtEndOfYear;
        }
      } else {
        if (hasStartKapitaal) {
          let E = Number(startingInvestmentCapital);
          for (let month = 0; month < actualEndMonth; month++) {
            E = E * (1 + INVESTMENT_RATE_MONTHLY);
          }
          investmentValueAtYearEnd = E;
        } else {
          investmentValueAtYearEnd = 0;
        }
      }
    }

    if (isPayoffYear) debtAtEndOfYear = 0;

    const totalPrincipalRepaidThisYear = totalDuoPaymentsThisYear - totalInterestThisYear;

    const fractionalMonth = payoffMonths - Math.floor(payoffMonths);
    const lastScheduleM = schedule.length > 0 ? schedule[schedule.length - 1].m : 0;
    const endMForYear = isPayoffYear ? lastScheduleM : yearEndMonthIndex;
    let actualLoanPaymentsThisYear = 0;
    let actualInvestmentThisYear = 0;
    for (let i = 0; i < schedule.length; i++) {
      const row = schedule[i];
      const m = row.m;
      if (m > yearStartMonth && m <= endMForYear) {
        const isLastRow = i === schedule.length - 1;
        const frac = isLastRow && fractionalMonth > 0 ? fractionalMonth : 1;
        actualLoanPaymentsThisYear += (row.loanPayment ?? 0) * frac;
        actualInvestmentThisYear += (row.investmentContribution ?? 0) * frac;
      }
    }

    data.push({
      year: yr,
      principal: debtAtBeginningOfYear,
      interest: cumulativeInterestAtYearEnd,
      investment: investmentValueAtYearEnd,
      payment,
      totalPaid: totalPaidThisYear,
      debtAtEnd: debtAtEndOfYear,
      totalPrincipalRepaid: totalPrincipalRepaidThisYear,
      actualLoanPaymentsThisYear,
      actualInvestmentThisYear,
    });

    debtAtBeginningOfYear = debtAtEndOfYear;

    if (
      debtAtEndOfYear <= 0.01 ||
      (enableInvestment && investmentValueAtYearEnd >= debtAtEndOfYear && debtAtEndOfYear > 0.01)
    ) {
      break;
    }
    if (yearEndMonthIndex >= payoffMonths) {
      break;
    }
  }

  const sEnd = sampleAtMonth(schedule, payoffMonths);
  const finalPrincipal = enableInvestment && payoffByInvestment ? sEnd.balance : 0;

  const isPayoffDuringDelay = enableDelay && payoffMonths < delayMonthsTotal;
  let finalPayment = 0;
  if (!isPayoffDuringDelay) {
    finalPayment = constrainedMonthlyPayment;
    if (enableGrowth) {
      const yearsElapsed = Math.max(0, Math.floor((payoffMonths - delayMonthsTotal) / 12));
      finalPayment = constrainedMonthlyPayment * Math.pow(1 + gr, yearsElapsed);
    }
  }

  const endIsIntegerYear = Math.abs(endYear - Math.round(endYear)) < 1e-9;
  const hasSameYearAlready = data.length > 0 && Math.abs(data[data.length - 1].year - endYear) < 1e-9;
  const lastRowPrincipal = data.length > 0 ? data[data.length - 1].principal || 0 : 0;
  const loanAlreadyPaidOff = lastRowPrincipal <= 0.01 && !(enableInvestment && payoffByInvestment);

  let finalYear = endYear;
  if (data.length > 0 && !endIsIntegerYear) {
    const lastIntegerYear = data[data.length - 1].year;
    const endYearFloor = Math.floor(endYear);
    if (lastIntegerYear === endYearFloor) {
      const decimalPart = endYear - endYearFloor;
      finalYear = lastIntegerYear + 1 + decimalPart;
    }
  }

  if (!(endIsIntegerYear && hasSameYearAlready) && !loanAlreadyPaidOff) {
    const cumulativeInterestAtFinal = interestAtEnd || sEnd.interestCumulative;

    let finalInvestmentValueFromSchedule = finalInvestmentValue;
    if (!finalInvestmentValueFromSchedule && schedule.length > 0) {
      for (let i = schedule.length - 1; i >= 0; i--) {
        if (schedule[i].m <= payoffMonths) {
          finalInvestmentValueFromSchedule = schedule[i].investmentValue || 0;
          break;
        }
      }
    }

    const fracMonth = payoffMonths - Math.floor(payoffMonths);
    const lastM = schedule.length > 0 ? schedule[schedule.length - 1].m : 0;
    const finalYearFloor = Math.floor(finalYear);
    let actualLoanFinal = 0;
    let actualInvestFinal = 0;
    for (let i = 0; i < schedule.length; i++) {
      const row = schedule[i];
      const m = row.m;
      if (m > finalYearFloor * 12 && m <= lastM) {
        const frac = i === schedule.length - 1 && fracMonth > 0 ? fracMonth : 1;
        actualLoanFinal += (row.loanPayment ?? 0) * frac;
        actualInvestFinal += (row.investmentContribution ?? 0) * frac;
      }
    }

    data.push({
      year: finalYear,
      principal: finalPrincipal,
      interest: cumulativeInterestAtFinal,
      investment: finalInvestmentValueFromSchedule || 0,
      payment: finalPayment,
      actualLoanPaymentsThisYear: actualLoanFinal,
      actualInvestmentThisYear: actualInvestFinal,
    });
  }

  return data;
}
